import React, { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useAppTheme } from '../contexts/ThemeContext';
import type { AppTheme } from '../theme/theme';

type LoadingSpinnerSize = 'small' | 'default' | 'large';
type LoadingSpinnerVariant = 'inline' | 'overlay' | 'fullHeight';

type LoadingSpinnerProps = {
  message?: ReactNode;
  size?: LoadingSpinnerSize;
  variant?: LoadingSpinnerVariant;
  style?: CSSProperties;
};

const getStyles = (theme: AppTheme): Record<string, CSSProperties> => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
    color: theme.colors.text.secondary,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${theme.colors.border}`,
    borderTop: `3px solid ${theme.colors.CORE}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinnerSmall: {
    width: '20px',
    height: '20px',
    border: `2px solid ${theme.colors.border}`,
    borderTop: `2px solid ${theme.colors.CORE}`,
  },
  spinnerLarge: {
    width: '60px',
    height: '60px',
    border: `4px solid ${theme.colors.border}`,
    borderTop: `4px solid ${theme.colors.CORE}`,
  },
  message: {
    marginLeft: theme.spacing.md,
    fontSize: theme.fontSize.xl,
    color: theme.colors.text.secondary,
  },
  messageSmall: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSize.md,
  },
  messageLarge: {
    marginLeft: theme.spacing.lg,
    fontSize: theme.fontSize.xxl,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  },
  inline: {
    padding: theme.spacing.md,
  },
  fullHeight: {
    height: '100%',
    minHeight: '200px',
  },
});

const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = spinnerStyles;
  document.head.appendChild(styleElement);
}

const LoadingSpinner = ({
  message = 'Loading...',
  size = 'default',
  variant = 'inline',
  style = {},
}: LoadingSpinnerProps) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const getSpinnerStyle = (sizeValue: LoadingSpinnerSize): CSSProperties => {
    switch (sizeValue) {
      case 'small':
        return styles.spinnerSmall;
      case 'large':
        return styles.spinnerLarge;
      default:
        return {};
    }
  };

  const getMessageStyle = (sizeValue: LoadingSpinnerSize): CSSProperties => {
    switch (sizeValue) {
      case 'small':
        return styles.messageSmall;
      case 'large':
        return styles.messageLarge;
      default:
        return {};
    }
  };

  const getContainerStyle = (variantValue: LoadingSpinnerVariant): CSSProperties => {
    switch (variantValue) {
      case 'overlay':
        return styles.overlay;
      case 'fullHeight':
        return { ...styles.container, ...styles.fullHeight };
      default:
        return { ...styles.container, ...styles.inline };
    }
  };

  return (
    <div style={{ ...getContainerStyle(variant), ...style }}>
      <div
        style={{
          ...styles.spinner,
          ...getSpinnerStyle(size),
        }}
      />
      {message && (
        <div
          style={{
            ...styles.message,
            ...getMessageStyle(size),
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
};

export default LoadingSpinner;
